import dayjs from "dayjs";

import { toDateInputValue } from "./format";

/** "Mar 2024" or "—". */
export const formatMonthYear = (value?: string | null): string => {
  const ymd = toDateInputValue(value);
  if (!ymd) return "—";
  const d = dayjs(ymd);
  return d.isValid() ? d.format("MMM YYYY") : "—";
};

/** "Mar 4, 2024" or "—". */
export const formatDayLabel = (value?: string | null): string => {
  const ymd = toDateInputValue(value);
  if (!ymd) return "—";
  const d = dayjs(ymd);
  return d.isValid() ? d.format("MMM D, YYYY") : "—";
};

/** "Mar 2024 – Present" / "Mar 2024 – Jun 2025". */
export const dateRangeLabel = (
  start?: string | null,
  end?: string | null,
  isCurrent?: boolean
): string => {
  const from = formatMonthYear(start);
  const to = isCurrent ? "Present" : formatMonthYear(end);
  return `${from} – ${to}`;
};

const msOf = (value?: string | null): number => {
  const ms = Date.parse(value ?? "");
  return Number.isFinite(ms) ? ms : 0;
};

/** Current entries first, then newest start date first. */
export const byCurrentThenStartDesc =
  <T>(
    getStart: (t: T) => string | null | undefined,
    isCurrent: (t: T) => boolean
  ) =>
  (a: T, b: T): number => {
    const ac = isCurrent(a);
    const bc = isCurrent(b);
    if (ac !== bc) return ac ? -1 : 1;
    return msOf(getStart(b)) - msOf(getStart(a));
  };

/** Newest date first. */
export const byDateDesc =
  <T>(getDate: (t: T) => string | null | undefined) =>
  (a: T, b: T): number =>
    msOf(getDate(b)) - msOf(getDate(a));

/** Among the entries flagged current, the one with the latest start. */
export const mostRecentCurrent = <T>(
  entries: T[],
  getStart: (t: T) => string | null | undefined,
  isCurrent: (t: T) => boolean
): T | null =>
  entries.filter(isCurrent).reduce<T | null>((best, entry) => {
    if (!best) return entry;
    return msOf(getStart(entry)) > msOf(getStart(best)) ? entry : best;
  }, null);
